import { useState } from "react";
import { Send, Check, Users } from "lucide-react";
import { submitRsvp } from "@/lib/api";

const initial = {
  name: "",
  email: "",
  phone: "",
  attending: "yes",
  guests: 1,
  event: "both",
  note: "",
};

export default function RSVPForm() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError("Please share your name ✨");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await submitRsvp({
        ...form,
        guests: Number(form.guests) || 1,
        email: form.email || null,
        phone: form.phone || null,
        note: form.note || null,
      });
      setDone(true);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          "Could not submit RSVP. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="rsvp-success"
        className="glass-card p-8 md:p-10 text-center"
      >
        <div className="mx-auto w-12 h-12 rounded-full border border-gold/50 flex items-center justify-center text-gold mb-4">
          <Check size={20} />
        </div>
        <h3 className="font-serif text-3xl gold-text">Thank you, {form.name}!</h3>
        <p className="mt-3 font-serif italic text-ivory-muted">
          Your RSVP is in. We can’t wait to celebrate #PandA with you ❤️
        </p>
        <button
          type="button"
          onClick={() => { setDone(false); setForm(initial); }}
          className="mt-6 btn-ghost-gold"
          data-testid="rsvp-another"
        >
          Submit another
        </button>
      </div>
    );
  }

  const inputCls =
    "w-full bg-ink-900/60 border border-gold/25 focus:border-gold/70 rounded-xl px-4 py-3 text-ivory placeholder:text-ivory-dim/60 outline-none transition-colors duration-300";
  const labelCls = "text-[0.65rem] uppercase tracking-[0.32em] text-ivory-dim mb-2 block";

  return (
    <form
      onSubmit={onSubmit}
      data-testid="rsvp-form"
      className="glass-card p-6 md:p-9 space-y-5"
    >
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className={labelCls}>Your Name *</label>
          <input
            data-testid="rsvp-name"
            value={form.name}
            onChange={update("name")}
            className={inputCls}
            placeholder="Your full name"
            required
          />
        </div>
        <div>
          <label className={labelCls}>Email</label>
          <input
            data-testid="rsvp-email"
            type="email"
            value={form.email}
            onChange={update("email")}
            className={inputCls}
            placeholder="you@example.com"
          />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className={labelCls}>Phone</label>
          <input
            data-testid="rsvp-phone"
            value={form.phone}
            onChange={update("phone")}
            className={inputCls}
            placeholder="Optional"
          />
        </div>
        <div>
          <label className={labelCls}>
            <Users size={11} className="inline -mt-0.5 mr-1" />
            Number of guests
          </label>
          <input
            data-testid="rsvp-guests"
            type="number"
            min={1}
            max={10}
            value={form.guests}
            onChange={update("guests")}
            className={inputCls}
          />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className={labelCls}>Will you attend?</label>
          <select
            data-testid="rsvp-attending"
            value={form.attending}
            onChange={update("attending")}
            className={`${inputCls} appearance-none cursor-pointer`}
          >
            <option value="yes">Joyfully accepting ✨</option>
            <option value="maybe">Trying my best</option>
            <option value="no">Sending love from afar</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>Which event?</label>
          <select
            data-testid="rsvp-event"
            value={form.event}
            onChange={update("event")}
            className={`${inputCls} appearance-none cursor-pointer`}
          >
            <option value="both">Both — Wedding &amp; Reception</option>
            <option value="wedding">Wedding (11:00 AM)</option>
            <option value="reception">Reception (7:30 PM)</option>
          </select>
        </div>
      </div>

      <div>
        <label className={labelCls}>A note for the couple (optional)</label>
        <textarea
          data-testid="rsvp-note"
          value={form.note}
          onChange={update("note")}
          rows={3}
          maxLength={400}
          className={`${inputCls} resize-none`}
          placeholder="Add a wish, a song request, anything ❤️"
        />
      </div>

      {error && (
        <p data-testid="rsvp-error" className="text-sm text-burgundy-glow">
          {error}
        </p>
      )}

      <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
        <p className="text-xs text-ivory-dim italic">
          Your details stay between us, with love.
        </p>
        <button
          type="submit"
          disabled={submitting}
          data-testid="rsvp-submit"
          className="btn-gold disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {submitting ? "Sending…" : "Send RSVP"} <Send size={13} />
        </button>
      </div>
    </form>
  );
}
